import React from 'react'

import { useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import Flex from 'styled-flex-component'

import { IModalComponentProps } from '@context/Modal'
import { useIsMounted } from '@hooks/useIsMounted'
import { IApplicationState } from '@store'
import {
  clearBrandFansErrors,
  IBrandFanEditableFields,
  selectBrandFanGroupName,
  selectBrandFansErrorCode,
  selectIsUpdatingBrandFanGroup,
} from '@store/brandFans'
import { updateBrandFanGroup } from '@store/brandFans/actions/updateBrandFanGroup'
import Theme from '@theme'
import { Button, Container, H1, Header, Modal, TextField } from '@tribegroup'
import BrandFansGroupModalErrorMessage from './ErrorMessage'

interface IProps extends IModalComponentProps {
  groupId: number
}

const BrandFansGroupModalRename: React.FC<IProps> = ({
  groupId,
  isOpen = false,
  onRequestClose,
}) => {
  const dispatch = useDispatch()
  const groupName = useSelector((state: IApplicationState) =>
    selectBrandFanGroupName(state, groupId),
  )
  const isUpdatingBrandFanGroup = useSelector((state: IApplicationState) =>
    selectIsUpdatingBrandFanGroup(state),
  )
  const errorCode = useSelector((state: IApplicationState) => selectBrandFansErrorCode(state))

  const {
    register,
    handleSubmit,
    formState,
    formState: { errors },
  } = useForm()

  // react-hook-form v7 returns { name, onChange, onBlur, ref } from register().
  const nameField = register('name', { required: true })

  const onSubmit = (params: IBrandFanEditableFields) => {
    dispatch(updateBrandFanGroup(groupId, params))
  }

  const hasError = Boolean(errorCode)

  const isMounted = useIsMounted()
  React.useEffect(() => {
    if (isMounted && !isUpdatingBrandFanGroup && !hasError) {
      onRequestClose()
    }
  }, [isUpdatingBrandFanGroup])

  const onClearErrors = () => {
    if (hasError) {
      dispatch(clearBrandFansErrors())
    }
  }
  React.useEffect(() => onClearErrors)

  return (
    <Modal reactModalProps={{ isOpen }} width={33.75} complex>
      <form data-testid="brand-fans-group-modal-rename-form" onSubmit={handleSubmit(onSubmit)}>
        <H1 color={Theme.grey900Color} bottomOuterSpacing={1.75} capitalize>
          <FormattedMessage id="brandFans.groups.actions.renameGroup" />
        </H1>
        <Container bottomOuterSpacing={4.375} relative>
          <TextField
            name={nameField.name}
            onBlur={nameField.onBlur}
            innerRef={nameField.ref}
            defaultValue={groupName}
            maxLength={60}
            fullWidth
            thick
            onChange={(event) => {
              void nameField.onChange(event)
              onClearErrors()
            }}
            autoFocus
            data-testid="brand-fan-group-modal-name"
          />
          {errors.name && (
            <BrandFansGroupModalErrorMessage>
              <FormattedMessage id="brandFans.error.brand_fan_group.update.name" />
            </BrandFansGroupModalErrorMessage>
          )}
          {hasError && (
            <BrandFansGroupModalErrorMessage>
              <FormattedMessage id={`brandFans.error.${errorCode}`} />
            </BrandFansGroupModalErrorMessage>
          )}
        </Container>
        <Flex justifyEnd alignCenter>
          <Button
            width={8.75}
            scheme="primary"
            height={3}
            outlined
            onClick={onRequestClose}
            disabled={isUpdatingBrandFanGroup}
            type="button"
          >
            <Header cta>
              <FormattedMessage id="core.text.cancel" />
            </Header>
          </Button>
          <Button
            width={8.75}
            scheme="primary"
            height={3}
            leftOuterSpacing={0.75}
            data-testid="brand-fans-group-update"
            disabled={isUpdatingBrandFanGroup || !formState.isDirty}
            loading={isUpdatingBrandFanGroup}
          >
            <Header cta>
              <FormattedMessage id="core.text.update" />
            </Header>
          </Button>
        </Flex>
      </form>
    </Modal>
  )
}

export { BrandFansGroupModalRename }
export default BrandFansGroupModalRename
